import React, { type ComponentProps, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { Loyalty } from '@order/features-loyalty';
import { useTelemetry } from '@order/Telemetry';

import { useLoyaltyOfferOptInMutation } from '../../graphql/LoyaltyOffers.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyOfferCard = (props: LoyaltyOfferCardProps) => {
  const { offer, rounded = false, layout, referrer, isInsideModal = false, onNavigate } = props;

  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { formatMessage } = useIntl();
  const router = useRouter();
  const { track } = useTelemetry();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ fetching }, optIn] = useLoyaltyOfferOptInMutation();

  // ─── Telemetry ───────────────────────────────────────────────────────

  const trackStartButtonPress = useCallback(() => {
    if (referrer === 'offers-modal') {
      track('loyalty.offer.modal.start.cta_tapped', { offerId: offer.id });
    }

    if (referrer === 'rewards') {
      track('loyalty.offer.card.order.cta_tapped', { offerId: offer.id });
    }
  }, [offer.id, referrer, track]);

  const trackOrderButtonPress = useCallback(() => {
    if (referrer === 'offers-modal') {
      track('loyalty.offer.modal.order.cta_tapped', { offerId: offer.id });
    }

    if (referrer === 'rewards') {
      track('loyalty.offer.card.order.cta_tapped', { offerId: offer.id });
    }
  }, [offer.id, referrer, track]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const showErrorBanner = useCallback(() => {
    const text = formatMessage(messages.errorBannerText);
    const shouldPreventDuplicates = true;

    addNoticeBanner(
      { id: ERROR_NOTICE_BANNER_ID, text, palette: 'caution' },
      shouldPreventDuplicates,
    );
  }, [addNoticeBanner, formatMessage]);

  const optInOffer = useCallback(async () => {
    trackStartButtonPress();

    const { data, error } = await optIn({ challengeId: offer.id });

    const responseTypename = data?.challengeOptInV2?.__typename;
    const hasFailedToOptedIn = responseTypename === 'LoyaltyOptInFailed';

    if (hasFailedToOptedIn || error) {
      showErrorBanner();
    }
  }, [offer.id, optIn, showErrorBanner, trackStartButtonPress]);

  const navigateToMenu = useCallback(() => {
    trackOrderButtonPress();
    onNavigate?.();

    if (isInsideModal) {
      router.dismissTo('/menu');

      return;
    }

    router.navigate('/menu');
  }, [isInsideModal, onNavigate, router, trackOrderButtonPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Loyalty.OfferCard
      offer={offer}
      isLoading={fetching}
      layout={layout}
      rounded={rounded}
      onStartButtonPress={optInOffer}
      onOrderButtonPress={navigateToMenu}
    />
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  errorBannerText: {
    defaultMessage: 'Failed to opt in to offer. Please try again later.',
    description: 'Loyalty | Offers | Opt in error banner text',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ERROR_NOTICE_BANNER_ID = 'LOYALTY_OFFER_OPT_IN_ERROR_BANNER';

// ─── Types ───────────────────────────────────────────────────────────────────

type LoyaltyOfferCardProps = {
  referrer?: 'rewards' | 'offers-modal';
  isInsideModal?: boolean;
  onNavigate?: () => void;
} & Pick<ComponentProps<typeof Loyalty.OfferCard>, 'offer' | 'rounded' | 'layout'>;
